<template>
  <div class="sidebar-container">
    <div 
      :class="['sidebar-wrapper', isExpanded ? 'expanded' : 'collapsed']"
    >
      <!-- Header with Logo -->
      <div class="sidebar-header">
        <!-- Close button when expanded -->
        <button
          v-if="isExpanded"
          @click="isExpanded = false; showLanguageSelector = false; updateSidebarState()"
          class="sidebar-close-btn"
        >
          <svg class="w-4 h-4" style="color: var(--sidebar-foreground)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div class="sidebar-logo">
          <svg class="w-5 h-5" style="color: var(--sidebar-primary-foreground)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </div>
        <div v-if="isExpanded" class="mt-3 text-center">
          <h2 class="font-semibold text-base whitespace-nowrap" style="color: var(--sidebar-foreground)">
            AITRA
          </h2>
          <p class="text-xs whitespace-nowrap mt-1" style="color: rgba(255, 255, 255, 0.7)">
            화상 강의 플랫폼
          </p>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="sidebar-nav">
        <div class="space-y-4">
          <div 
            v-for="item in navigationItems" 
            :key="item.id" 
            class="relative group nav-item-wrapper"
          >
            <button
              @click="handleNavigationClick(item.id)"
              :class="[
                'nav-item',
                isExpanded ? 'expanded' : 'collapsed',
                currentPage === item.id ? 'active' : ''
              ]"
            >
              <!-- Home Icon -->
              <svg v-if="item.id === 'dashboard'" class="nav-icon" :class="currentPage === item.id ? 'active' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9,22 9,12 15,12 15,22"/>
              </svg>
              
              <!-- Reports Icon -->
              <svg v-else-if="item.id === 'reports'" class="nav-icon" :class="currentPage === item.id ? 'active' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              
              <!-- Profile Icon -->
              <!-- <svg v-else-if="item.id === 'profile'" class="nav-icon" :class="currentPage === item.id ? 'active' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg> -->
              
              <div v-if="isExpanded" class="nav-text">
                <div :class="[
                  'nav-text-content',
                  currentPage === item.id ? 'active' : ''
                ]">
                  {{ item.name }}
                </div>
                <div v-if="currentPage === item.id" class="nav-description">
                  {{ item.description }}
                </div>
              </div>
              
              <!-- Active indicator for collapsed state -->
              <template v-if="currentPage === item.id && !isExpanded">
                <div class="active-indicator-collapsed" />
                <div class="active-indicator-line" />
              </template>
              
              <!-- Active indicator for expanded state -->
              <div 
                v-if="currentPage === item.id && isExpanded" 
                class="active-indicator-dot"
              />
            </button>

            <!-- Tooltip for collapsed state -->
            <div 
              v-if="!isExpanded" 
              class="nav-tooltip"
            >
              <div class="font-medium text-sm">{{ item.name }}</div>
              <div class="text-xs opacity-75 mt-1">{{ item.description }}</div>
              <div class="nav-tooltip-arrow" />
            </div>
          </div>
          
          <!-- Language Selector - Profile 아래에 배치 -->
          <div class="relative group language-item-wrapper nav-item-wrapper">
            <button
              @click="handleLanguageClick"
              :class="[
                'nav-item',
                isExpanded ? 'expanded' : 'collapsed',
                'language-nav-item'
              ]"
            >
              <!-- Language Icon -->
              <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              
              <!-- Google Translate Component - 지구본 오른쪽에 바로 표시 (넓은 사이드바) -->
              <div v-if="isExpanded" class="language-selector-inline-right">
                <GoogleTranslate />
              </div>
            </button>
            
            <!-- Tooltip for collapsed state - 버튼 밖으로 이동 -->
            <div 
              v-if="!isExpanded" 
              class="nav-tooltip"
            >
              <div class="font-medium text-sm">언어 설정</div>
              <div class="text-xs opacity-75 mt-1">번역</div>
              <div class="nav-tooltip-arrow" />
            </div>
            
            <!-- Collapsed state에서 언어 선택기 - 위로 표시 -->
            <div 
              v-if="!isExpanded && showLanguageSelector" 
              class="language-selector-wrapper-internal"
              @click.stop
            >
              <GoogleTranslate />
            </div>
          </div>
        </div>
      </nav>

      <!-- Bottom Actions -->
      <div class="sidebar-footer">
        <!-- Logout Button -->
        <div class="flex justify-center">
          <button 
            @click="handleLogout"
            class="footer-btn logout"
          >
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
        
        <!-- User Profile -->
        <div class="relative group flex justify-center nav-item-wrapper">
          <div 
            class="footer-btn profile cursor-pointer"
            @click="handleProfileClick"
          >
            <span class="font-semibold text-sm" style="color: var(--sidebar-primary-foreground)">{{ userInitial }}</span>
          </div>
          
          <!-- Profile tooltip for collapsed state -->
          <div 
            v-if="!isExpanded" 
            class="profile-tooltip"
          >
            <div class="font-medium text-sm">{{ userName || '사용자' }}</div>
            <!-- <div class="text-xs opacity-75 mt-1">강사</div> -->
            <div class="nav-tooltip-arrow" />
          </div>
          
          <!-- Profile info for expanded state -->
          <!-- <div v-if="isExpanded" class="profile-info-expanded">
            <div class="profile-name">
              {{ userName || '사용자' }}
            </div>
            <div class="profile-role">
              강사
            </div>
          </div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineOptions, ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useRouter, useRoute } from 'vue-router'
import GoogleTranslate from '@/components/GoogleTranslate.vue'


const { logout, userName, user } = useAuth()
const router = useRouter()
const route = useRoute()

// Sidebar state
const isExpanded = ref(false)
const showLanguageSelector = ref(false)

// 언어 변경 이벤트 리스너
const handleLanguageChange = () => {
  // GoogleTranslate 컴포넌트에서 언어 변경 시 이벤트 발생
  // 언어 변경은 GoogleTranslate 컴포넌트에서 직접 처리됨
}

// Language selector 외부 클릭 감지 (축소 상태에서만 필요)
const handleClickOutside = (event) => {
  if (isExpanded.value) {
    // 확장된 상태에서는 드롭다운이 항상 표시되므로 외부 클릭 감지 불필요
    return
  }
  
  const languageButton = event.target.closest('.nav-item')
  const languageWrapper = event.target.closest('.language-selector-wrapper-internal')
  
  // 언어 버튼인지 확인 (Profile 아래의 nav-item)
  const isLanguageButton = languageButton && languageButton.querySelector('svg')?.querySelector('circle[cx="12"]')
  
  if (!isLanguageButton && !languageWrapper && showLanguageSelector.value) {
    showLanguageSelector.value = false
  }
}

// 드롭다운이 위로 열리도록 조정
const adjustDropdownPosition = () => {
  // body에 추가되는 모든 popper를 관찰
  const observer = new MutationObserver(() => {
    // 언어 선택기와 관련된 popper 찾기
    const languageSelector = document.querySelector('.language-selector-inline-right')
    if (!languageSelector) return
    
    const selectEl = languageSelector.querySelector('.el-select')
    if (!selectEl) return
    
    // body에 있는 모든 popper 중에서 언어 선택기와 관련된 것 찾기
    const allPoppers = document.querySelectorAll('body > .el-popper')
    allPoppers.forEach((popper) => {
      const placement = popper.getAttribute('data-popper-placement') || ''
      // bottom placement인 경우 위로 변경
      if (placement.startsWith('bottom')) {
        const selectWrapper = selectEl.querySelector('.el-select__wrapper')
        if (selectWrapper) {
          const wrapperRect = selectWrapper.getBoundingClientRect()
          // 드롭다운을 버튼 위로 배치
          popper.style.top = `${wrapperRect.top - popper.offsetHeight - 8}px`
          popper.style.bottom = 'auto'
          popper.style.left = `${wrapperRect.left}px`
          popper.style.transform = 'none'
        }
      }
    })
  })
  
  // body 전체를 관찰 (popper가 body에 추가되므로)
  observer.observe(document.body, {
    childList: true,
    subtree: false
  })
  
  // 주기적으로도 확인 (드롭다운이 열릴 때를 대비)
  const interval = setInterval(() => {
    observer.disconnect()
    const selectEl = document.querySelector('.language-selector-inline-right .el-select')
    if (selectEl) {
      const poppers = document.querySelectorAll('body > .el-popper')
      poppers.forEach((popper) => {
        if (popper.style.display !== 'none') {
          const selectWrapper = selectEl.querySelector('.el-select__wrapper')
          if (selectWrapper) {
            const wrapperRect = selectWrapper.getBoundingClientRect()
            const popperRect = popper.getBoundingClientRect()
            // 드롭다운이 select 아래에 있으면 위로 이동
            if (popperRect.top > wrapperRect.bottom) {
              popper.style.top = `${wrapperRect.top - popper.offsetHeight - 8}px`
              popper.style.bottom = 'auto'
              popper.style.left = `${wrapperRect.left}px`
              popper.style.transform = 'none'
            }
          }
        }
      })
    }
    observer.observe(document.body, {
      childList: true,
      subtree: false
    })
  }, 100)
  
  return { observer, interval }
}

let dropdownWatcher = null

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // 언어 변경 이벤트 리스너
  document.addEventListener('setLanguage', handleLanguageChange)
  
  // 드롭다운 위치 조정
  dropdownWatcher = adjustDropdownPosition()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('setLanguage', handleLanguageChange)
  
  // Observer 및 interval 정리
  if (dropdownWatcher) {
    if (dropdownWatcher.observer) {
      dropdownWatcher.observer.disconnect()
    }
    if (dropdownWatcher.interval) {
      clearInterval(dropdownWatcher.interval)
    }
  }
})

// user의 language 변경 감지 (computed 속성이므로 자동으로 반영됨)
watch(() => user.value?.language, () => {
  // 언어가 변경되면 currentLanguage computed 속성이 자동으로 업데이트됨
})

// Emit sidebar state to parent
const emit = defineEmits(['sidebar-toggle'])
const updateSidebarState = () => {
  emit('sidebar-toggle', isExpanded.value)
}

// Current page detection
const currentPage = computed(() => {
  const routeName = route.name
  if (routeName === 'dashboard') return 'dashboard'
  if (routeName === 'Reports') return 'reports'  // 라우트 이름이 'Reports' (대문자)
  // if (routeName === 'profile') return 'profile'
  return 'dashboard'
})

// User initial for profile button
const userInitial = computed(() => {
  if (userName.value && userName.value.length > 0) {
    return userName.value.charAt(0).toUpperCase()
  }
  return 'U' // Default fallback
})

// Navigation items
const navigationItems = [
  {
    id: "dashboard",
    name: "Home",
    description: "메인 대시보드"
  },
  {
    id: "reports",
    name: "Reports",
    description: "강의 리포트"
  }
  // {
  //   id: "profile",
  //   name: "Profile",
  //   description: "내 프로필"
  // }
]

const handleNavigationClick = (pageId) => {
  // Profile 페이지는 아직 준비중
  // if (pageId === 'profile') {
  //   alert('준비중입니다.')
  //   return
  // }
  
  if (!isExpanded.value) {
    isExpanded.value = true
    updateSidebarState()
    // Delay the page change to allow expansion animation
    setTimeout(() => {
      router.push(`/${pageId}`)
    }, 150)
  } else {
    router.push(`/${pageId}`)
  }
}

const handleLogout = () => {
  logout()
  router.push('/login')
}

// Profile button click handler
const handleProfileClick = () => {
  // Profile 페이지로 이동 시도
  // if (currentPage.value !== 'profile') {
  //   alert('준비중입니다.')
  //   return
  // }
  
  const wasExpanded = isExpanded.value
  isExpanded.value = !isExpanded.value
  // 사이드바가 축소될 때 언어 선택기도 닫기
  if (wasExpanded && !isExpanded.value) {
    showLanguageSelector.value = false
  }
  updateSidebarState()
}

// Language button click handler (아이콘 클릭)
const handleLanguageClick = () => {
  if (!isExpanded.value) {
    // 사이드바가 축소되어 있으면 먼저 확장
    isExpanded.value = true
    updateSidebarState()
    // 확장되면 드롭다운이 자동으로 표시됨
  }
  // 확장된 상태에서는 아이콘 클릭 시 특별한 동작 없음 (드롭다운이 항상 표시됨)
}

defineOptions({
  name: 'MainNavigation'
})
</script>

<style scoped>
/* 기존 스타일은 main.css에서 정의된 클래스들을 사용 */
</style>